/**
 * The four districts Mysore Yellow Pages actively covers, and the taluks
 * (sub-districts) inside each one. This is the single source of truth for
 * "which districts are in scope" — tourist spots and location listings are
 * filtered against it, so anything outside these four districts never shows.
 *
 * The district names match the values used by the tourist-spot data
 * (e.g. "Kodagu (Coorg)"), while taluks mirror the `towns` records in the
 * database so the frontend and backend stay in step.
 */

export interface Taluk {
  /** Display name of the taluk, e.g. "Nanjangud". */
  name: string;
  /** URL-safe slug, matching the database `towns.slug`. */
  slug: string;
}

export interface CoveredRegion {
  /** Canonical district name (matches tourist-spot districts). */
  district: string;
  /** Slug used by the locations API for this district. */
  slug: string;
  taluks: Taluk[];
}

export const COVERED_REGIONS: CoveredRegion[] = [
  {
    district: "Mysore",
    slug: "mysore",
    taluks: [
      { name: "Mysuru", slug: "mysore-city" },
      { name: "Nanjangud", slug: "nanjangud" },
      { name: "Hunsur", slug: "hunsur" },
      { name: "Heggadadevankote (H.D. Kote)", slug: "heggadadevankote" },
      { name: "Krishnarajanagara (K.R. Nagar)", slug: "krishnarajanagara" },
      { name: "Piriyapatna", slug: "periyapatna" },
      { name: "T. Narasipura", slug: "t-narasipura" },
    ],
  },
  {
    district: "Mandya",
    slug: "mandya",
    taluks: [
      { name: "Mandya", slug: "mandya" },
      { name: "Maddur", slug: "maddur" },
      { name: "Malavalli", slug: "malavalli" },
      { name: "Srirangapatna", slug: "srirangapatna" },
      { name: "Krishnarajpet (K.R. Pet)", slug: "krishnarajpet" },
      { name: "Nagamangala", slug: "nagamangala" },
      { name: "Pandavapura", slug: "pandavapura" },
    ],
  },
  {
    district: "Chamarajanagar",
    slug: "chamarajanagar",
    taluks: [
      { name: "Chamarajanagar", slug: "chamarajanagar" },
      { name: "Gundlupet", slug: "gundlupet" },
      { name: "Kollegal", slug: "kollegal" },
      { name: "Yelandur", slug: "yelandur" },
    ],
  },
  {
    district: "Kodagu (Coorg)",
    slug: "kodagu-coorg",
    taluks: [
      { name: "Madikeri", slug: "madikeri" },
      { name: "Virajpet", slug: "virajpet" },
      { name: "Somwarpet", slug: "somwarpet" },
      { name: "Kushalnagar", slug: "kushalnagar" },
      { name: "Ponnampet", slug: "ponnampet" },
    ],
  },
];

/** District display names in coverage order (Mysore, Mandya, Chamarajanagar, Kodagu). */
export const COVERED_DISTRICTS = COVERED_REGIONS.map((r) => r.district);

/** Normalises a district name so "Kodagu" and "Kodagu (Coorg)" compare equal. */
export function normalizeDistrict(name: string | undefined | null): string {
  if (!name) return "";
  return name
    .replace(/\s*\(coorg\)/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** True when a district is one of the four we cover. */
export function isCoveredDistrict(name: string | undefined | null): boolean {
  const normalized = normalizeDistrict(name);
  if (!normalized) return false;
  return COVERED_REGIONS.some((r) => normalizeDistrict(r.district) === normalized);
}

/** The taluks inside a district (empty when the district isn't covered). */
export function taluksForDistrict(name: string | undefined | null): Taluk[] {
  const normalized = normalizeDistrict(name);
  if (!normalized) return [];
  return COVERED_REGIONS.find((r) => normalizeDistrict(r.district) === normalized)?.taluks ?? [];
}

/** A taluk with the district it belongs to, for grouped/global taluk lists. */
export interface TalukWithDistrict extends Taluk {
  district: string;
  districtSlug: string;
}

/** Every taluk across all four covered districts, flattened. */
export const ALL_TALUKS: TalukWithDistrict[] = COVERED_REGIONS.flatMap((region) =>
  region.taluks.map((taluk) => ({
    ...taluk,
    district: region.district,
    districtSlug: region.slug,
  })),
);

/** District display name for a district slug, or "" when not covered. */
export function districtNameForSlug(slug: string | undefined | null): string {
  if (!slug) return "";
  return COVERED_REGIONS.find((r) => r.slug === slug)?.district ?? "";
}

/** Taluk display name for a taluk slug, or "" when unknown. */
export function talukNameForSlug(slug: string | undefined | null): string {
  if (!slug) return "";
  return ALL_TALUKS.find((t) => t.slug === slug)?.name ?? "";
}

/** The district slug a taluk belongs to, or "" when unknown. */
export function districtSlugForTaluk(slug: string | undefined | null): string {
  if (!slug) return "";
  return ALL_TALUKS.find((t) => t.slug === slug)?.districtSlug ?? "";
}